<template>
  <ais-instant-search
    ref="instantSearch"
    :search-client="searchClient"
    :index-name="index"
    :insights-client="insightsClient"
    :middlewares="middlewares"
    :search-parameters="{ clickAnalytics: true }"
  >
    <ais-configure
      :hits-per-page.camel="500"
      :distinct="true"
      :facet-filters.camel="facetFilters()"
    />

    <wa-page view-slug="category-landing">
      <wa-page-header slot="header" />

      <template v-if="category != null && notFound == false">
        <main
          slot="content"
          class="app-content"
          :class="`theme-${colors.color1}`"
          role="main"
        >
          <wa-icons-loading v-if="isLoading" />

          <div v-else>
            <div class="category-header">
              <div class="container">
                <category-header
                  :version="version"
                  :category="categoryLabel"
                  :category-icon="categoryIcon"
                  :description="categoryDescription"
                  :selected-style="selectedStyle"
                  :selected-family="selectedFamily"
                />
              </div>
            </div>

            <div class="container">
              <div class="row roomy">
                <div class="column-12 padding-y-xl">
                  <wa-old-version-alert
                    v-if="isOlderVersion"
                    subject="icons"
                  />

                  <div class="row roomy align-between align-top margin-top-xl">
                    <div class="column-12 margin-bottom-xl tablet:margin-bottom-0 tablet:column-8">
                      <div>
                        <icon-pack-selector
                          :category-icon="categoryIcon"
                          :selected-style="params.style"
                          :selected-family="params.family"
                          :selected-pack="params.pack"
                          :version="version"
                          @selectStyle="selectStyle"
                        />

                        <input
                          id="checkbox_free"
                          v-model="isFree"
                          type="checkbox"
                          name="checkbox_free"
                          class="sr-only with-icon"
                          value="Show only Free icons"
                        />
                        <label
                          for="checkbox_free"
                          class="icons-facet icons-facet-input margin-0 flex-row size-sm flex-items-baseline"
                          style="padding: var(--spacing-2xs) var(--spacing-sm); cursor: pointer; display: flex !important"
                        >
                          <span class="position-relative margin-right-xs">
                            <font-awesome-icon
                              :icon="$v6.solid.faSquareCheck"
                              fixed-width
                              size="lg"
                              class="checked-icon"
                              style="color: var(--category-color-alt)"
                            />
                            <font-awesome-icon
                              :icon="$v6.regular.faSquare"
                              fixed-width
                              size="lg"
                              class="hover-icon"
                            />
                            <font-awesome-icon
                              :icon="$v6.regular.faSquare"
                              fixed-width
                              size="lg"
                              class="unchecked-icon"
                            />
                          </span>
                          <span> Show only Free icons </span>
                        </label>
                      </div>
                    </div>

                    <div class="column-12 tablet:column-4 margin-top-4xs">
                      <form
                        class="icons-search form"
                        method="post"
                        name="header_search"
                        @submit.prevent="search()"
                      >
                        <div
                          class="with-icon-before"
                          style="--input-padding: var(--spacing-sm) var(--spacing-xl) var(--spacing-xs) var(--spacing-xl)"
                        >
                          <font-awesome-icon
                            :icon="$v6.solid.faSearch"
                            style="line-height: 1; top: 13px"
                          />
                          <label
                            for="search_query"
                            class="sr-only"
                          >
                            Search Icons
                          </label>
                          <input
                            id="search_query"
                            ref="searchQuery"
                            name="search_query"
                            value=""
                            :placeholder="`Search icons in ${categoryLabel}...`"
                            type="text"
                            class="rounded"
                            @focus="searchFieldFocus = true"
                            @blur="searchFieldFocus = false"
                          />
                        </div>
                        <button
                          type="submit"
                          class="sr-only"
                        >
                          Search Font Awesome Icons
                        </button>
                      </form>
                    </div>
                  </div>

                  <category-icons
                    ref="iconList"
                    :category-label="categoryLabel"
                    :selected-icon-id="selectedIconId"
                    :selected-style="params.style"
                    :selected-family="params.family"
                    :is-pro-user="isProUser"
                    :free-selected="isFree"
                    :version="version"
                    :search-field-focus="searchFieldFocus"
                    @toggleIcon="toggleIcon"
                  />

                  <category-examples
                    :category="category"
                    :category-label="categoryLabel"
                    :category-icon="categoryIcon"
                    :selected-family="selectedFamily"
                    :version="version"
                  />

                  <div
                    id="more"
                    class="margin-top-3xl"
                  >
                    <article>
                      <div class="row align-middle margin-bottom-xl">
                        <div class="column-12 margin-bottom-sm laptop:column-7 laptop:margin-bottom-0">
                          <h3 class="h3 text-capitalize">Not finding what you need in {{ categoryLabel }}?</h3>
                        </div>

                        <div class="column-12 laptop:column-5 laptop:text-right">
                          <p>That's okay, we've got options for that too.</p>
                        </div>
                      </div>

                      <icons-supplemental-actions
                        :version="version"
                        layout-wide
                      />
                    </article>

                    <article>
                      <div class="row align-middle margin-bottom-xl">
                        <div class="column-7">
                          <h3 class="h3 text-capitalize margin-0">Or try out these other categories&hellip;</h3>
                        </div>

                        <div class="column-5 text-right">
                          <site-link
                            :to="{ name: version === 5 ? 'five.icons.categories' : 'latest.icons.categories' }"
                            class="button flat with-border margin-bottom-0"
                            style="--border-color: var(--fa-gravy); --button-background: transparent; --button-color: var(--fa-navy)"
                            @click.native="$posthog.capture('category_landing:all_categories_link_click', { category: category })"
                          >
                            View All Categories
                            <span class="display-none tablet:display-inline">
                              <font-awesome-icon
                                :icon="$v6.solid.faGrid2"
                                fixed-width
                                size="lg"
                                class="margin-left-md"
                              />
                            </span>
                          </site-link>
                        </div>
                      </div>

                      <category-suggestions
                        :category-icons="categoryIconsList"
                        :suggestion-count="6"
                        :current-category="category"
                        :color="colors.color1"
                      />
                    </article>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <section
            id="iconPreview"
            class="wrap-icon-detail"
          >
            <transition
              name="toggle-icon-detail"
              appear
              enter-active-class="animate__animated animate__faster animate__fadeIn"
              @after-leave="ejectIconPreview"
            >
              <modal-icon-detail
                ref="iconModal"
                :default-styles="defaultStyles"
                :icon="icon"
                :icon-abstract="iconAbstract"
                :icon-name="selectedIconId"
                :index="index"
                :is-duotone="isDuotoneIcon"
                :is-pro-user="isProUser"
                :is-showing="isShowingIconModal"
                :selected-from-search="selectedIcon"
                :styling="styling"
                :viggo="viggo"
                @closeIcon="closeIcon"
                @nextIcon="$refs.iconList.next()"
                @prevIcon="$refs.iconList.previous()"
                @resetStyles="resetStyles"
                @selectCategory="selectCategory"
                @stylingChange="stylingChange"
              />
            </transition>
          </section>
        </main>

        <font-awesome-summary
          slot="sock"
          :categories-count="categoriesCount"
          :version="version"
        />
      </template>

      <main
        v-else
        slot="content"
        class="app-content"
        role="main"
      >
        <category-not-found
          :categories-count="categoriesCount"
          :category="category"
        />
      </main>

      <wa-page-footer slot="footer" />
    </wa-page>
  </ais-instant-search>
</template>

<script>
  import { merge, startCase } from 'lodash'
  import ModalIconDetail from './components/icons/details/ModalIconDetail'
  import CategoryExamples from './components/categories/CategoryExamples'
  import CategoryHeader from './components/categories/CategoryHeader'
  import CategorySuggestions from './components/categories/CategorySuggestions'
  import IconsSupplementalActions from './components/icons/IconsSupplementalActions'
  import CategoryIcons from './components/categories/CategoryIcons'
  import CategoryNotFound from './components/categories/CategoryNotFound'
  import IconPackSelector from './components/categories/IconPackSelector'
  import FontAwesomeSummary from './components/icons/FontAwesomeSummary'
  import algoliasearch from 'algoliasearch/lite'

  import { aa, createInsightsMiddleware } from '../search/insightUtils'
  import { DEFAULT_STYLING } from 'app/views/icons/defaults'
  import { EventTrackingMixin, SubscriptionMixin } from 'app/mixins'
  import { fetchIndexName } from '../search/utils'
  import { fetchSvg } from 'app/views/icons/components/icons/details/utils'
  import { IconsMixins } from 'app/views/icons/components/icons/mixins.js'
  import { loadViggo } from 'app/viggo'
  import { stylingToQuery } from 'app/views/icons/routing'
  import { getDefaultIconPack } from 'app/views/search/collections.js'

  const categoryIconsV6 = require('app/views/icons/category-figures/v6.json')
  const categoryIconsV5 = require('app/views/icons/category-figures/v5.json')
  const categoryColors = require('./components/categories/categoryColors.json')

  export default {
    name: 'CategoryLanding',

    components: {
      CategoryExamples,
      CategoryHeader,
      CategoryIcons,
      CategorySuggestions,
      CategoryNotFound,
      FontAwesomeSummary,
      IconPackSelector,
      IconsSupplementalActions,
      ModalIconDetail
    },

    mixins: [EventTrackingMixin, SubscriptionMixin, IconsMixins],

    data() {
      return {
        colors: null,
        insightsClient: aa,
        icon: null,
        iconAbstract: null,
        isLoading: false,
        isFree: false,
        middlewares: [createInsightsMiddleware()],
        notFound: false,
        params: this.queryToParams(this.$route.query),
        previewMode: 'day',
        resultsView: 'compact',
        searchClient: algoliasearch(FONTAWESOME.ALGOLIA_APPLICATION_ID, FONTAWESOME.ALGOLIA_SEARCH_API_KEY),
        searchFieldFocus: false,
        selectedIcon: null,
        selectedIconId: null,
        selectedStyle: this.$route.query.s || 'solid',
        selectedFamily: this.$route.query.f || 'classic',
        isShowingIconModal: false,
        styling: {
          animation: DEFAULT_STYLING.animation,
          colorPrimary: DEFAULT_STYLING.colorPrimary,
          colorSecondary: DEFAULT_STYLING.colorSecondary,
          family: 'classic',
          opacityPrimary: DEFAULT_STYLING.opacityPrimary,
          opacitySecondary: DEFAULT_STYLING.opacitySecondary,
          rotation: DEFAULT_STYLING.rotation,
          size: DEFAULT_STYLING.size,
          style: 'solid'
        },
        viggo: null
      }
    },

    computed: {
      defaultStyles() {
        const stylingDefaults = { ...DEFAULT_STYLING }

        stylingDefaults.family = this.styling.family
        stylingDefaults.style = this.styling.style

        return stylingDefaults
      },

      index() {
        return fetchIndexName(this.$releases.active.version)
      },

      isOlderVersion() {
        return this.$releases.active.versionParts.major != 6 ? true : false
      },

      version() {
        return this.$releases.active.versionParts.major
      },

      categoryIconsList() {
        return this.version == 5 ? categoryIconsV5 : categoryIconsV6
      },

      categoryIcon() {
        const icon = this.categoryIconsList.filter((c) => {
          return c.id == this.category
        })[0]
        return icon ? icon.icon : ''
      },

      categoryDescription() {
        const icon = this.categoryIconsList.filter((c) => {
          return c.id == this.category
        })[0]
        return icon ? icon.description : ''
      },

      categoryLabel() {
        const icon = this.categoryIconsList.filter((c) => {
          return c.id == this.category
        })[0]
        return icon ? icon.label : ''
      },

      category() {
        return decodeURIComponent(this.$route.path.split('/').pop())
      },

      pageTitle() {
        if (this.params.style === 'solid') {
          return `${startCase(this.category)} Icons`
        } else {
          return `${startCase(this.category)} Icons - ${startCase(this.params.style)}`
        }
      },

      isProUser() {
        return this.marketing.hasActiveProSubscription
      },

      categoriesCount() {
        return this.categoryIconsList.length
      }
    },

    watch: {
      styling(newValue, oldValue) {
        if (!this.selectedIconId) {
          /* No icon selected, we should go back to search */
          this.routeBackToSearch()
        }

        if (this.selectedIconId && (newValue.family !== oldValue.family || newValue.style !== oldValue.style)) {
          /* Icon selected and the family and style have changed */
          this.routeToIconDetail()
        }

        if (this.selectedIconId && newValue.family === oldValue.family && newValue.style === oldValue.style) {
          /* A styling option changed */
          this.routeWithDifferentStyling()
        }

        /*
         * watching and checking to see if the family or style has changed
         * if changed, we need to fetch the new svg
         * fetchSvg(), in this instance, is ultimately used to get the iconAbstract
         * and pass along to the codeSnippetSvg.vue component
         */
        if (this.selectedIcon && (newValue.family !== oldValue.family || newValue.style !== oldValue.style)) {
          fetchSvg(this.viggo, false, this.selectedIcon, this.$releases.active.version, this.styling)
            .then((iconAbstract) => {
              this.iconAbstract = iconAbstract
            })
            .catch((error) => {
              throw new Error('fetchSvg() in watcher', error)
            })
        }
      },

      icon(newValue) {
        if (newValue !== null) {
          document.title = `${newValue.name} Icon | Font Awesome`
        } else {
          document.title = this.originalTitle
        }
      }
    },

    beforeMount() {
      this.isLoading = true
      setTimeout(() => {
        this.isLoading = false
      }, 300)
    },

    created() {
      if (!this.validateCategory()) {
        this.notFound = true
      }

      this.colors = this.getRandomItem(categoryColors)

      const defaultPack = getDefaultIconPack(this.version)

      if (this.params.style == '') {
        this.params.style = defaultPack.icons[0].style
      }

      if (this.params.family == '') {
        this.params.family = defaultPack.icons[0].family
      }

      if (this.params.pack == '') {
        this.params.pack = defaultPack.name
      }
    },

    mounted() {
      this.$posthog.capture('category_landing_view', merge({ category: this.nameSlug(this.categoryLabel) }, this.determinationsEventProps))
    },

    methods: {
      routeWithHistory({ url, using } = { using: history.pushState }) {
        using.call(history, {}, null, url)
      },

      routeBackToSearch() {
        /*
         * We are cheating here and using this.$route because it will be set to whatever
         * the last search parameters were. Since we use the History object directly for
         * this Instragram-style navigation we end up bypassing Vue Router. So conveniently
         * this.$route is equal to the last time Vue Router was in control which happens to
         * be when the search page was loaded or last interacted with.
         */
        this.routeWithHistory({
          using: history.replaceState,
          url: this.$router.resolve({
            name: this.$route.name,
            params: this.$route.params,
            query: this.$route.query
          }).href
        })
      },

      routeToIconDetail({ using } = { using: history.pushState }) {
        this.routeWithHistory({
          using,
          url: this.$router.resolve({
            name: this.$versionRouteName('icons.detail'),
            params: { icon: this.selectedIconId },
            query: stylingToQuery(this.styling)
          }).href
        })
      },

      routeWithDifferentStyling() {
        this.routeToIconDetail({ using: history.replaceState })
      },

      resetStyles() {
        this.styling = { ...this.styling, ...DEFAULT_STYLING }
      },

      stylingChange(styling) {
        this.styling = { ...this.styling, ...styling }
      },

      validateCategory() {
        return this.categoryIconsList.some((c) => {
          return c.id == this.category
        })
      },

      facetFilters() {
        const filters = []
        filters.push(`categories:${this.category}`)
        filters.push(`family:${this.params.family}`)

        if (this.params.style !== 'all') {
          filters.push(`style:${this.params.style}`)
        }

        return filters
      },

      queryToParams(query) {
        const { s = '', f = '', p = '' } = query
        return {
          style: s,
          family: f,
          pack: p
        }
      },

      selectStyle(style, family, pack) {
        if (this.params.style !== style || this.params.pack !== pack || this.params.family !== family) {
          this.closeIcon()

          this.params.style = style
          this.params.family = family
          this.params.pack = pack
          this.$posthog.capture('category_landing:icon_style_button_click', { family: family, style: style, pack: pack, category: this.category })
          this.$router.push({ query: { f: family, s: style, p: pack } })
        }
      },

      search() {
        this.$router.push(this.$router.resolve(this.$search({ categories: [this.category], term: this.$refs.searchQuery.value })).href)
        this.$posthog.capture('category_landing:search_form_submit', { term: this.$refs.searchQuery.value, category: this.category })
      },

      async toggleIcon(icon) {
        if (this.selectedIconId === icon.name && this.selectedStyle === icon.style && this.selectedFamily === icon.family) {
          this.closeIcon()
        } else {
          if (!this.viggo) {
            try {
              this.viggo = await loadViggo()
            } catch (error) {
              throw new Error('Error loading viggo', error)
            }
          }

          this.selectedIcon = icon
          this.selectedIconId = icon.name
          this.selectedStyle = icon.style
          this.selectedFamily = icon.family
          this.styling = { ...this.styling, family: icon.family, style: icon.style === '' && icon.family === 'brands' ? 'solid' : icon.style }

          this.$refs.iconModal.closeEditor()
          this.isShowingIconModal = true
          this.$posthog.capture('category_landing:icon_listing_click', {
            category: this.categoryLabel,
            icon_name: this.selectedIconId,
            icon_family: this.selectedFamily,
            icon_style: this.selectedStyle
          })
        }

        try {
          this.iconAbstract = await fetchSvg(this.viggo, false, icon, this.$releases.active.version, this.styling)
        } catch (error) {
          throw new Error('fetchingSvg()', error)
        }
      },

      closeIcon() {
        if (this.selectedIconId) {
          this.selectedIcon = null
          this.selectedIconId = null
          this.isShowingIconModal = false
          this.styling = { ...this.styling, ...DEFAULT_STYLING, family: null, style: null }
        }
      },

      ejectIconPreview() {
        const iconPreview = document.getElementById('iconPreview')

        if (iconPreview != null) {
          document.getElementById('searchContent').insertAdjacentElement('afterend', iconPreview)
        }
      },

      setPreviewMode(mode) {
        this.previewMode = mode
      },

      getRandomItem(items) {
        return items[Math.floor(Math.random() * items.length)]
      },

      selectCategory(category) {
        this.setSearchTerm('')
        this.selectedCategory = category
      }
    }
  }
</script>
